import React, { useState, useEffect, useRef } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Progress,
  Tag,
  Select,
  Table,
  Avatar,
  Tooltip,
  Divider,
  Space,
  Alert,
  Input,
  Empty,
} from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Button,
  EyeIcon,
  FilesPdfIcon,
  Flex,
  Loader,
  StarIcon,
} from "@fluentui/react-northstar";
import Chart from "react-apexcharts";
import { api } from "../../../../../../Assets/api/api";
import { useAssignment } from "../../../../../../context/AssignmentContext";
import { useTranslation } from "react-i18next";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const { Title, Text } = Typography;
const { Option } = Select;

const brandColor = "#6264a7";

// Reusable Card with Title
const DashboardCard = ({ title, extra, children, ...props }) => (
  <Card
    title={<span style={{ fontWeight: 600 }}>{title}</span>}
    extra={extra}
    size="small"
    {...props}
    bodyStyle={{ height: "100%", flexGrow: 1 }}
  >
    {children}
  </Card>
);

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Analytics component error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <Alert
            message="Component Error"
            description="Something went wrong loading this component"
            type="error"
            showIcon
          />
        )
      );
    }

    return this.props.children;
  }
}

// Add API fetch function with better error handling
const fetchAnalyticsData = async (assignmentId, user) => {
  try {
    if (!assignmentId || !user?.slug) {
      throw new Error("Missing required parameters");
    }

    const response = await api.get(
      `/api/assignment/get-analytics-of-assignment-grading${user.slug}&assignmentId=${assignmentId}`
    );

    if (!response?.data) {
      throw new Error("No data received from API");
    }

    const data = response.data;
    if (data.status === "success" && data.results) {
      return data.results;
    }
    throw new Error(data.message || "Failed to fetch analytics data");
  } catch (error) {
    console.error("Error fetching analytics data:", error);
    throw error;
  }
};

// Helper function to get alert icon with error handling
const getAlertIcon = (iconName) => {
  const icons = {
    ExclamationCircle: <ExclamationCircleOutlined />,
    CheckCircle: <CheckCircleOutlined />,
    Info: <InfoCircleOutlined />,
    Eye: <EyeIcon />,
  };
  return icons[iconName] || <InfoCircleOutlined />;
};

// KeyMetrics component with error handling
const KeyMetrics = ({ data }) => {
  const { t } = useTranslation();
  const translation = t("assignment").summary.analytics.keyMetrics;

  if (!data?.keyMetrics) {
    return null;
  }

  const {
    gradeDistribution,
    aiOverrideRate,
    gradingTime,
    personalizedFeedback,
  } = data.keyMetrics;

  // Validate required data
  if (
    !gradeDistribution ||
    !aiOverrideRate ||
    !gradingTime ||
    !personalizedFeedback
  ) {
    return (
      <Alert
        message="Data Error"
        description="Some metrics data is missing or invalid"
        type="warning"
        showIcon
      />
    );
  }

  const donutOptions = {
    chart: { type: "donut", sparkline: { enabled: true } },
    colors: ["#10B981", "#F59E0B", "#EF4444"],
    legend: { show: false },
    dataLabels: { enabled: true },
    plotOptions: { pie: { donut: { size: "70%" } } },
    stroke: { width: 0 },
    tooltip: { enabled: true },
  };

  const donutSeries = aiOverrideRate.breakdown
    ? [
        aiOverrideRate.breakdown[0]?.percentage || 0,
        aiOverrideRate.breakdown[1]?.percentage || 0,
        aiOverrideRate.breakdown[2]?.percentage || 0,
      ]
    : [0, 0, 0];

  const feedbackOptions = {
    chart: { type: "radialBar", sparkline: { enabled: true } },
    colors: ["#8B5CF6"],
    plotOptions: {
      radialBar: {
        hollow: { size: "70%" },
        track: { background: "#E5E7EB" },
        dataLabels: {
          name: { show: false },
          value: {
            fontSize: "1.5rem",
            fontWeight: 600,
            color: "#8B5CF6",
            offsetY: 5,
            formatter: (val) => val + "%",
          },
        },
      },
    },
    stroke: { lineCap: "round" },
  };

  const feedbackSeries = [personalizedFeedback.percentage];

  return (
    <Row gutter={[8, 8]} style={{ margin: 0 }}>
      {/* Grade Distribution */}
      <Col xs={24} md={6}>
        <Card style={{ height: "100%" }}>
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Text strong>{translation.gradeDistribution}</Text>
            {gradeDistribution.trend.value > 0 && (
              <Text
                style={{
                  color:
                    gradeDistribution.trend.direction === "up"
                      ? "#10B981"
                      : "#EF4444",
                  fontWeight: 500,
                }}
              >
                {gradeDistribution.trend.direction === "up" ? (
                  <ArrowUpOutlined />
                ) : (
                  <ArrowDownOutlined />
                )}
                {gradeDistribution.trend.value}%
              </Text>
            )}
          </Space>
          <div style={{ margin: "16px 0 8px 0" }}>
            <Title
              level={2}
              style={{ margin: 0, color: "#222", textAlign: "start" }}
            >
              {gradeDistribution.averageGrade}
            </Title>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              height: 80,
              gap: 8,
            }}
          >
            {gradeDistribution.distribution.map((item, index) => (
              <div
                key={item.grade}
                style={{
                  width: "20%",
                  height: `${item.percentage}%`,
                  background:
                    index === 0
                      ? "#10B981"
                      : index === 1
                      ? "#A3E635"
                      : index === 2
                      ? "#FDE047"
                      : index === 3
                      ? "#FDBA74"
                      : "#FCA5A5",
                  borderRadius: 4,
                }}
              />
            ))}
          </div>
        </Card>
      </Col>

      {/* AI Override Rate */}
      <Col xs={24} md={6}>
        <Card style={{ height: "100%" }}>
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Text strong>{translation.aiOverrideRate}</Text>
            {aiOverrideRate.trend.value > 0 && (
              <Text
                style={{
                  color:
                    aiOverrideRate.trend.direction === "up"
                      ? "#EF4444"
                      : "#10B981",
                  fontWeight: 500,
                }}
              >
                {aiOverrideRate.trend.direction === "up" ? (
                  <ArrowUpOutlined />
                ) : (
                  <ArrowDownOutlined />
                )}
                {Math.abs(aiOverrideRate.trend.value)}%
              </Text>
            )}
          </Space>
          <div style={{ margin: "16px 0 8px 0" }}>
            <Title level={2} style={{ margin: 0, color: "#222" }}>
              {aiOverrideRate.currentRate}%
            </Title>
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Chart
              options={donutOptions}
              series={donutSeries}
              type="donut"
              height={130}
            />
          </div>
        </Card>
      </Col>

      {/* Grading Time */}
      <Col xs={24} md={6}>
        <Card style={{ height: "100%" }}>
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Text strong>{translation.avgTimeToGrade}</Text>
            {gradingTime.improvement.percentage > 0 && (
              <Text style={{ color: "#2563eb", fontWeight: 500 }}>
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                {gradingTime.improvement.percentage}%{" "}
                {gradingTime.improvement.description}
              </Text>
            )}
          </Space>
          <div style={{ margin: "16px 0 8px 0" }}>
            <span style={{ fontSize: 32, fontWeight: 700, color: "#222" }}>
              {gradingTime.averageTime}
            </span>
            <span style={{ fontSize: 18, color: "#888", marginLeft: 4 }}>
              {gradingTime.unit}
            </span>
          </div>
          <div style={{ marginTop: 32 }}>
            <Progress
              percent={gradingTime.progressPercentage}
              showInfo={false}
              strokeColor="#2563eb"
              style={{ height: 12, borderRadius: 8 }}
            />
          </div>
        </Card>
      </Col>

      {/* Personalized Feedback */}
      <Col xs={24} md={6}>
        <Card style={{ height: "100%" }}>
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Text strong>{translation.personalizedFeedback}</Text>
            {personalizedFeedback.trend.value > 0 && (
              <Text style={{ color: "#8B5CF6", fontWeight: 500 }}>
                <StarIcon style={{ marginRight: 4 }} />+
                {personalizedFeedback.trend.value}%
              </Text>
            )}
          </Space>
          <div style={{ margin: "16px 0 8px 0" }}>
            <Title level={2} style={{ margin: 0, color: "#222" }}>
              {personalizedFeedback.percentage}%
            </Title>
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Chart
              options={feedbackOptions}
              series={feedbackSeries}
              type="radialBar"
              height={130}
            />
          </div>
        </Card>
      </Col>
    </Row>
  );
};

// RubricPerformance component with error handling
const RubricPerformance = ({ data }) => {
  const { t } = useTranslation();
  const translation = t("assignment").summary.analytics.rubricPerformance;

  if (!data?.rubricPerformance?.criteria) {
    return (
      <DashboardCard title={translation.title} style={{ minHeight: 180 }}>
        <Alert
          message={translation.noRubricData}
          description={translation.noRubricDataDesc}
          type="info"
          showIcon
        />
      </DashboardCard>
    );
  }

  const { rubricPerformance } = data;

  return (
    <DashboardCard
      title={translation.title}
      // extra={
      //   <Select size="small" defaultValue="All Courses" style={{ width: 120 }}>
      //     <Option value="all">All Courses</Option>
      //   </Select>
      // }
      style={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Flex column style={{ height: "100%", flexGrow: 1 }}>
        <div style={{ flexGrow: 1 }}>
          {rubricPerformance.criteria.map((item) => (
            <div key={item.label}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 4,
                }}
              >
                <Text
                  style={{ fontSize: 14, fontWeight: 500, color: "#374151" }}
                >
                  {item.label}
                </Text>
                <Text
                  style={{ fontSize: 14, fontWeight: 500, color: "#374151" }}
                >
                  {item.value}% met
                </Text>
              </div>
              <Progress
                percent={item.value}
                showInfo={false}
                strokeColor="#2563eb"
                trailColor="#e5e7eb"
                strokeWidth={10}
                style={{ marginBottom: 8 }}
              />
            </div>
          ))}
        </div>

        <Divider style={{ margin: "16px 0" }} />

        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {translation.highestPerforming}
            </Text>
            <div style={{ fontSize: 14, fontWeight: 500, color: "#1f2937" }}>
              {rubricPerformance.summary.highestPerforming}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {translation.needsImprovement}
            </Text>
            <div style={{ fontSize: 14, fontWeight: 500, color: "#1f2937" }}>
              {rubricPerformance.summary.needsImprovement}
            </div>
          </div>
        </div>
      </Flex>
    </DashboardCard>
  );
};

// Strategic Alerts component with error handling
const StrategicAlerts = ({ data }) => {
  const { t } = useTranslation();
  const translation = t("assignment").summary.analytics.strategicAlerts;

  if (!data?.strategicAlerts?.alerts) {
    return (
      <DashboardCard title={translation.title}>
        <Alert
          message={translation.noAlerts}
          description={translation.noAlertsDesc}
          type="info"
          showIcon
        />
      </DashboardCard>
    );
  }

  return (
    <DashboardCard
      title={translation.title}
      extra={
        <Select
          size="small"
          defaultValue="View all alerts"
          style={{ width: 120 }}
        >
          <Option>View all alerts</Option>
        </Select>
      }
    >
      <Row gutter={[16, 16]}>
        {data.strategicAlerts.alerts.map((alert, index) => (
          <Col span={12} key={index}>
            <Alert
              message={alert.title}
              description={alert.description}
              type={alert.type}
              showIcon
              icon={getAlertIcon(alert.icon)}
              style={{ flex: 1 }}
              action={
                alert.actions && (
                  <Space>
                    {alert.actions.map((action, i) => (
                      <Button key={i} size="small">
                        {action}
                      </Button>
                    ))}
                  </Space>
                )
              }
            />
          </Col>
        ))}
      </Row>
    </DashboardCard>
  );
};

const RadarChart = ({ data }) => {
  const { t } = useTranslation();
  const translation = t("assignment").summary.analytics.radarChart;

  if (!data?.rubricPerformance?.criteria) {
    return (
      <DashboardCard title={translation.title} style={{ minHeight: "100%" }}>
        <Alert
          message={translation.noData}
          description={translation.noDataDesc}
          type="info"
          showIcon
        />
      </DashboardCard>
    );
  }

  const { criteria } = data.rubricPerformance;

  const options = {
    chart: {
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    plotOptions: {
      radar: {
        polygons: {
          strokeColors: "#e8e8e8",
          fill: {
            colors: ["#f8fafc", "#f0f9ff"],
          },
        },
      },
    },
    colors: ["#6264a7"],
    stroke: {
      width: 2,
    },
    fill: {
      opacity: 0.2,
    },
    markers: {
      size: 4,
      colors: ["#fff"],
      strokeColors: ["#6264a7"],
      strokeWidth: 2,
    },
    yaxis: {
      show: true,
      min: 0,
      max: 100,
      tickAmount: 5,
    },
    xaxis: {
      categories: criteria.map((item) => item.label),
    },
  };

  const series = [
    {
      name: "Performance",
      data: criteria.map((item) => item.value),
    },
  ];

  return (
    <DashboardCard
      title={translation.title}
      // extra={
      //   <Space>
      //     <Select
      //       size="small"
      //       defaultValue="By Department"
      //       style={{ width: 120 }}
      //     >
      //       <Option>By Department</Option>
      //       <Option>By Course</Option>
      //       <Option>By Assignment</Option>
      //     </Select>
      //     <a href="#">Compare</a>
      //   </Space>
      // }
      style={{ minHeight: 420 }}
    >
      <Text type="secondary" style={{ marginBottom: 16, display: "block" }}>
        {translation.averageScores}
      </Text>

      <div
        style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}
      >
        <Chart options={options} series={series} type="radar" height={320} />
      </div>

      <div
        style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}
      >
        <Space>
          <div
            style={{
              width: 16,
              height: 8,
              backgroundColor: "#6264a7",
              borderRadius: 2,
            }}
          />
          <Text type="secondary">{translation.currentPerformance}</Text>
        </Space>
      </div>

      <Space>
        <div
          style={{
            width: 8,
            height: 8,
            backgroundColor: "#ef4444",
            borderRadius: "50%",
          }}
        />
        <Text type="secondary">
          {translation.lowest}
          {
            criteria.reduce((min, item) =>
              item.value < min.value ? item : min
            ).label
          }{" "}
          (
          {
            criteria.reduce((min, item) =>
              item.value < min.value ? item : min
            ).value
          }
          %)
        </Text>
      </Space>
    </DashboardCard>
  );
};

// Grading Trends Over Time (Mock)
const GradingTrends = () => {
  const options = {
    chart: {
      type: "line",
      toolbar: {
        show: false,
      },
      dropShadow: {
        enabled: true,
        top: 5,
        left: 0,
        blur: 3,
        opacity: 0.2,
      },
    },
    colors: ["#3b82f6", "#10b981"],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      curve: "smooth",
      width: 3,
    },
    grid: {
      borderColor: "#e2e8f0",
      row: {
        colors: ["#f8fafc", "transparent"],
        opacity: 0.5,
      },
    },
    markers: {
      size: 5,
      colors: ["#fff"],
      strokeColors: ["#3b82f6", "#10b981"],
      strokeWidth: 2,
    },
    xaxis: {
      categories: ["Week 1", "Week 2", "Week 3", "Week 4", "Week 5", "Week 6"],
      labels: {
        style: {
          colors: "#64748b",
          fontSize: "12px",
        },
      },
    },
    yaxis: {
      min: 60,
      max: 100,
      tickAmount: 5,
      labels: {
        style: {
          colors: "#64748b",
          fontSize: "12px",
        },
      },
    },
    legend: {
      show: false,
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.1,
        inverseColors: false,
        opacityFrom: 0.9,
        opacityTo: 0.6,
      },
    },
  };

  const series = [
    {
      name: "English 101",
      data: [72, 75, 78, 82, 80, 85],
    },
    {
      name: "History 201",
      data: [68, 70, 72, 75, 73, 78],
    },
  ];

  return (
    <DashboardCard
      title="Grading Trends Over Time"
      extra={
        <Space>
          <Select
            size="small"
            defaultValue="Instructor View"
            style={{ width: 140 }}
          >
            <Option value="instructor">Instructor View</Option>
            <Option value="department">Department View</Option>
            <Option value="student">Student View</Option>
          </Select>
          <Button size="small" icon={<DownloadOutlined />}>
            Filter
          </Button>
        </Space>
      }
      style={{ minHeight: "100%" }}
    >
      <Text type="secondary" style={{ marginBottom: 16, display: "block" }}>
        Average assignment scores by week
      </Text>

      <div style={{ position: "relative", height: 350 }}>
        <Chart options={options} series={series} type="line" height={350} />

        <div
          style={{
            position: "absolute",
            top: 16,
            right: 16,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Space size="large">
            <Space>
              <div
                style={{
                  width: 16,
                  height: 8,
                  backgroundColor: "#3b82f6",
                  borderRadius: 2,
                }}
              />
              <Text type="secondary">English 101</Text>
            </Space>
            <Space>
              <div
                style={{
                  width: 16,
                  height: 8,
                  backgroundColor: "#10b981",
                  borderRadius: 2,
                }}
              />
              <Text type="secondary">History 201</Text>
            </Space>
          </Space>
        </div>
      </div>
    </DashboardCard>
  );
};

// Grading Trends Table (Mock)
const GradingTrendsTable = () => (
  <DashboardCard
    title="Grading Trends"
    extra={
      <Select size="small" defaultValue="Last 6 Months" style={{ width: 120 }}>
        <Option>Last 6 Months</Option>
        <Option>Last 3 Months</Option>
        <Option>Last Month</Option>
      </Select>
    }
    style={{ minHeight: 320 }}
  >
    <div style={{ position: "relative", height: 256, marginBottom: 16 }}>
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 24,
          right: 0,
          height: 224,
          display: "flex",
          alignItems: "flex-end",
          gap: 8,
        }}
      >
        {[
          { month: "Jan", height: "40%" },
          { month: "Feb", height: "55%" },
          { month: "Mar", height: "47%" },
          { month: "Apr", height: "61%" },
          { month: "May", height: "70%" },
          { month: "Jun", height: "80%" },
        ].map((item) => (
          <div
            key={item.month}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <div
              style={{
                height: item.height,
                backgroundColor: brandColor,
                borderTopLeftRadius: 2,
                borderTopRightRadius: 2,
              }}
            />
            <Text style={{ fontSize: 12, textAlign: "center", marginTop: 4 }}>
              {item.month}
            </Text>
          </div>
        ))}
      </div>

      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 24,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        {["100%", "75%", "50%", "25%", "0%"].map((label) => (
          <Text key={label} type="secondary" style={{ fontSize: 12 }}>
            {label}
          </Text>
        ))}
      </div>
    </div>

    <Divider style={{ margin: "16px 0" }} />

    <Row gutter={16}>
      <Col span={8}>
        <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
          Average Score
        </Text>
        <Text strong style={{ fontSize: 18 }}>
          82%
        </Text>
      </Col>
      <Col span={8}>
        <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
          Monthly Change
        </Text>
        <Text type="success" strong style={{ fontSize: 18 }}>
          +3.8%
        </Text>
      </Col>
      <Col span={8}>
        <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
          Assignments
        </Text>
        <Text strong style={{ fontSize: 18 }}>
          1,342
        </Text>
      </Col>
    </Row>
  </DashboardCard>
);

// Instructor Insights Table (Mock)
const InstructorInsights = () => (
  <DashboardCard
    title="Instructor Insights"
    extra={
      <Space>
        <Input
          size="small"
          placeholder="Search instructors..."
          style={{ width: 180 }}
          suffix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
        />
        <Select size="small" defaultValue="name" style={{ width: 150 }}>
          <Option value="name">Sort by: Name</Option>
          <Option value="department">Sort by: Department</Option>
          <Option value="grade">Sort by: Grade Average</Option>
          <Option value="override">Sort by: Override Rate</Option>
        </Select>
      </Space>
    }
    style={{ minHeight: 420 }}
  >
    <Table
      size="small"
      pagination={{
        total: 42,
        pageSize: 4,
        showSizeChanger: false,
      }}
      columns={[
        {
          title: "Instructor",
          dataIndex: "instructor",
          render: (name, record) => (
            <Space>
              <Avatar
                style={{
                  backgroundColor: record.avatarColor,
                  color: record.avatarTextColor,
                }}
              >
                {record.initials}
              </Avatar>
              <div>
                <Text strong>{name}</Text>
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {record.department}
                </Text>
              </div>
            </Space>
          ),
        },
        {
          title: "Courses",
          dataIndex: "courses",
          width: 80,
        },
        {
          title: "Students",
          dataIndex: "students",
          width: 80,
        },
        {
          title: "Avg Grade",
          dataIndex: "avgGrade",
          render: (grade, record) => (
            <div>
              <Text>{grade}</Text>
              <br />
              <Progress
                percent={record.gradePercent}
                size="small"
                showInfo={false}
                strokeColor={record.gradeColor}
                style={{ width: 60 }}
              />
            </div>
          ),
        },
        {
          title: "AI Override Rate",
          dataIndex: "override",
          render: (rate, record) => (
            <div>
              <Text>{rate}%</Text>
              <br />
              <Progress
                percent={rate}
                size="small"
                showInfo={false}
                strokeColor={rate < 15 ? "#6264a7" : "#f5222d"}
                style={{ width: 60 }}
              />
            </div>
          ),
        },
        {
          title: "Grading Time",
          dataIndex: "time",
          render: (time, record) => (
            <div>
              <Text>{time}</Text>
              <br />
              <Text type="success" style={{ fontSize: 12 }}>
                <ArrowUpOutlined /> {record.speedImprovement}
              </Text>
            </div>
          ),
        },
        {
          title: "Status",
          dataIndex: "status",
          render: (status) => {
            const color = {
              Optimal: "success",
              Watch: "warning",
              Alert: "error",
            }[status];
            return <Tag color={color}>{status}</Tag>;
          },
        },
      ]}
      dataSource={[
        {
          key: 1,
          instructor: "Dr. Jane Davis",
          initials: "JD",
          department: "Computer Science",
          avatarColor: "#e6f4ff",
          avatarTextColor: "#1890ff",
          courses: 3,
          students: 68,
          avgGrade: "B+ (87%)",
          gradePercent: 87,
          gradeColor: "#52c41a",
          override: 8.2,
          time: "5.3 min",
          speedImprovement: "58% faster",
          status: "Optimal",
        },
        {
          key: 2,
          instructor: "Dr. Robert Martinez",
          initials: "RM",
          department: "Engineering",
          avatarColor: "#f9f0ff",
          avatarTextColor: "#722ed1",
          courses: 4,
          students: 122,
          avgGrade: "A- (92%)",
          gradePercent: 92,
          gradeColor: "#52c41a",
          override: 18.7,
          time: "7.2 min",
          speedImprovement: "41% faster",
          status: "Watch",
        },
        {
          key: 3,
          instructor: "Sarah Johnson",
          initials: "SJ",
          department: "Liberal Arts",
          avatarColor: "#f6ffed",
          avatarTextColor: "#52c41a",
          courses: 2,
          students: 45,
          avgGrade: "B (83%)",
          gradePercent: 83,
          gradeColor: "#52c41a",
          override: 6.4,
          time: "3.8 min",
          speedImprovement: "72% faster",
          status: "Optimal",
        },
        {
          key: 4,
          instructor: "Dr. Michael Thomas",
          initials: "MT",
          department: "Business Administration",
          avatarColor: "#fff1f0",
          avatarTextColor: "#f5222d",
          courses: 3,
          students: 87,
          avgGrade: "C+ (77%)",
          gradePercent: 77,
          gradeColor: "#faad14",
          override: 28.3,
          time: "9.1 min",
          speedImprovement: "25% faster",
          status: "Alert",
        },
      ]}
    />
  </DashboardCard>
);

// Student Writing Progress (Mock)
const StudentWritingProgress = () => (
  <DashboardCard
    title="Student Writing Progress"
    extra={
      <Flex gap="gap.small">
        <Select
          size="small"
          defaultValue="Fall Semester 2023"
          style={{ width: 160 }}
        >
          <Option value="fall2023">Fall Semester 2023</Option>
          <Option value="spring2023">Spring Semester 2023</Option>
        </Select>
        <Select
          size="small"
          defaultValue="All Departments"
          style={{ width: 160 }}
        >
          <Option value="all">All Departments</Option>
          <Option value="cs">Computer Science</Option>
          <Option value="eng">Engineering</Option>
        </Select>
      </Flex>
    }
  >
    <div style={{ overflowX: "auto" }}>
      <div style={{ minWidth: "max-content" }}>
        <Flex>
          <div style={{ width: 128 }}></div>
          {[
            "Week 1",
            "Week 2",
            "Week 3",
            "Week 4",
            "Week 5",
            "Week 6",
            "Week 7",
            "Week 8",
          ].map((week) => (
            <Text
              key={week}
              size="small"
              color="gray"
              style={{ flex: 1, textAlign: "center", marginBottom: 4 }}
            >
              {week}
            </Text>
          ))}
        </Flex>

        {[
          {
            course: "CS 101",
            instructor: "Dr. Davis",
            progress: [1, 2, 3, 4, 5, 6, 7, 8],
          },
          {
            course: "ENG 201",
            instructor: "Dr. Martinez",
            progress: [2, 3, 4, 4, 5, 6, 6, 7],
          },
          {
            course: "HIST 301",
            instructor: "Sarah Johnson",
            progress: [1, 2, 3, 3, 4, 5, 6, 7],
          },
          {
            course: "BUS 402",
            instructor: "Dr. Thomas",
            progress: [1, 1, 2, 2, 3, 4, 5, 5],
          },
        ].map((row, i) => (
          <Flex
            key={i}
            style={{ marginBottom: 8, height: 56, alignItems: "center" }}
          >
            <div style={{ width: 128, paddingRight: 8 }}>
              <Text weight="semibold">{row.course}</Text>
              <Text size="small" color="gray">
                {row.instructor}
              </Text>
            </div>
            {row.progress.map((level, j) => (
              <div key={j} style={{ flex: 1, padding: 4 }}>
                <div
                  style={{
                    height: "100%",
                    backgroundColor: `var(--blue-${level}00)`,
                    borderRadius: 6,
                  }}
                />
              </div>
            ))}
          </Flex>
        ))}
      </div>
    </div>

    <Flex hAlign="center" style={{ marginTop: 16 }}>
      <Flex gap="gap.smaller" vAlign="center">
        {[1, 2, 3, 4, 5, 6, 7, 8].map((level) => (
          <div
            key={level}
            style={{
              width: 12,
              height: 12,
              backgroundColor:
                level === 1
                  ? "#e6f4ff"
                  : level === 2
                  ? "#bae0ff"
                  : level === 3
                  ? "#91caff"
                  : level === 4
                  ? "#69b1ff"
                  : level === 5
                  ? "#4096ff"
                  : level === 6
                  ? "#1677ff"
                  : level === 7
                  ? "#0958d9"
                  : "#003eb3",
              borderRadius: 4,
            }}
          />
        ))}
        <Text size="small" color="gray" style={{ marginLeft: 8 }}>
          Lower → Higher Writing Proficiency
        </Text>
      </Flex>
    </Flex>

    <Flex
      gap="gap.small"
      style={{
        marginTop: 16,
        paddingTop: 16,
        borderTop: "1px solid var(--gray-200)",
      }}
    >
      <Alert
        message="Overall Trend"
        description="Average writing proficiency increased by 38% over the semester"
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ flex: 1 }}
      />
      <Alert
        message="Best Performer"
        description="CS 101 showed the most consistent improvement week over week"
        type="success"
        showIcon
        icon={<CheckCircleOutlined />}
        style={{ flex: 1 }}
      />
      <Alert
        message="Needs Attention"
        description="BUS 402 is showing slower progression compared to other courses"
        type="warning"
        showIcon
        icon={<WarningOutlined />}
        style={{ flex: 1 }}
      />
    </Flex>
  </DashboardCard>
);

// Main Analytics component with comprehensive error handling
const Analytics = ({ user }) => {
  const { t } = useTranslation();
  const translation = t("assignment").summary.analytics;
  const { currentAssignmentData } = useAssignment();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const analyticsRef = useRef();

  const handleExportPDF = async () => {
    try {
      if (!analyticsRef.current) {
        console.error("Content analyticsRef not found");
        return;
      }
      const canvas = await html2canvas(analyticsRef.current, {
        useCORS: true,
        scale: 4,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
      pdf.save("analytics.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (!currentAssignmentData?.id || !user) {
        setError("Missing required parameters");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await fetchAnalyticsData(currentAssignmentData?.id, user);
        setAnalyticsData(data);
      } catch (err) {
        setError(err.message || "Failed to load analytics data");
        setAnalyticsData(null);
      } finally {
        setLoading(false);
      }
    };

    currentAssignmentData?.id && user && loadData();
  }, [currentAssignmentData, user]);

  if (loading) {
    return (
      <div style={{ padding: 16, textAlign: "center" }}>
        <Loader label="Loading analytics data..." />
      </div>
    );
  }

  return analyticsData ? (
    <Flex
      column
      gap="gap.medium"
      style={{ padding: 16, background: "#f0f2f5" }}
    >
      <Row gutter={[16, 16]} style={{ margin: 0 }}>
        <Col xs={24}>
          <Flex space="between" gap="gap.large" vAlign="center">
            <div>
              <Title level={4} style={{ margin: 0 }}>
                {translation.dashboardTitle}
              </Title>
              <Text type="secondary">
                {translation.sampled}{" "}
                {analyticsData.metadata?.lastUpdated
                  ? new Date(
                      analyticsData.metadata.lastUpdated
                    ).toLocaleString()
                  : "Date not available"}
              </Text>
            </div>
            <div style={{ float: "right" }}>
              {/* <Select
                  size="small"
                  defaultValue={
                    analyticsData.metadata?.timeRange || "Last 30 Days"
                  }
                  style={{ width: 120 }}
                >
                  <Option value="Last 30 Days">Last 30 Days</Option>
                  <Option value="Last 60 Days">Last 60 Days</Option>
                  <Option value="This Semester">This Semester</Option>
                  <Option value="Last Semester">Last Semester</Option>
                  <Option value="All Time">All Time</Option>
                </Select>
                <Select
                  size="small"
                  defaultValue={
                    analyticsData.metadata?.department || "All Departments"
                  }
                  style={{ width: 140 }}
                >
                  <Option value="All Departments">All Departments</Option>
                  <Option value="English">English</Option>
                  <Option value="History">History</Option>
                  <Option value="Science">Science</Option>
                  <Option value="Mathematics">Mathematics</Option>
                </Select>
                <Select
                  size="small"
                  defaultValue={analyticsData.metadata?.course || "All Courses"}
                  style={{ width: 120 }}
                >
                  <Option>
                    {analyticsData.metadata?.course || "All Courses"}
                  </Option>
                </Select> */}
              <Button
                icon={<FilesPdfIcon />}
                onClick={handleExportPDF}
                content={translation.download}
              />
            </div>
          </Flex>
        </Col>
      </Row>

      <div
        style={{
          height: "calc(100vh - 390px)",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <div ref={analyticsRef}>
          <ErrorBoundary
            fallback={
              <Alert
                message="Error in Key Metrics"
                description="Failed to display key metrics"
                type="error"
                showIcon
              />
            }
          >
            <KeyMetrics data={analyticsData} />
          </ErrorBoundary>
          <div style={{ marginTop: "8px" }}>
            <Row gutter={[8, 8]} style={{ margin: 0 }}>
              <Col xs={24} md={12}>
                <ErrorBoundary
                  fallback={
                    <Alert
                      message="Error in Rubric Performance"
                      description="Failed to display rubric performance."
                      type="error"
                      showIcon
                    />
                  }
                >
                  <RadarChart data={analyticsData} />
                </ErrorBoundary>
              </Col>
              {/* <Col xs={24} md={12}>
            <ErrorBoundary
              fallback={
                <Alert
                  message="Error in Radar Chart"
                  description="Failed to display radar chart"
                  type="error"
                  showIcon
                />
              }
            >
              <GradingTrends data={analyticsData} />
            </ErrorBoundary>
          </Col> */}
              <Col xs={24} md={12}>
                <ErrorBoundary
                  fallback={
                    <Alert
                      message="Error in Grading Trends"
                      description="Failed to display grading trends"
                      type="error"
                      showIcon
                    />
                  }
                >
                  <RubricPerformance data={analyticsData} />
                </ErrorBoundary>
              </Col>
              {/* <Col xs={24} md={12}>
            <ErrorBoundary
              fallback={
                <Alert
                  message="Error in Grading Trends Table"
                  description="Failed to display grading trends table"
                  type="error"
                  showIcon
                />
              }
            >
              <GradingTrendsTable data={analyticsData} />
            </ErrorBoundary>
          </Col> */}
              {/* <Col xs={24} md={24}>
            <ErrorBoundary
              fallback={
                <Alert
                  message="Error in Instructor Insights"
                  description="Failed to display instructor insights"
                  type="error"
                  showIcon
                />
              }
            >
              <InstructorInsights data={analyticsData} />
            </ErrorBoundary>
          </Col> */}
              {/* <Col xs={24} md={24}>
            <ErrorBoundary
              fallback={
                <Alert
                  message="Error in Student Writing Progress"
                  description="Failed to display student writing progress"
                  type="error"
                  showIcon
                />
              }
            >
              <StudentWritingProgress data={analyticsData} />
            </ErrorBoundary>
          </Col> */}
            </Row>
          </div>
        </div>
      </div>
    </Flex>
  ) : (
    <Flex hAlign="center" vAlign="center" style={{ marginTop: 200 }}>
      <Empty description="Analytics data is not available at this time" />
    </Flex>
  );
};

export default Analytics;
